/**
 * Portfolio metric catalog (EPD-10133 / EPD-10114 / EPD-10115 / EPD-10116).
 *
 * The single source of truth for which metrics each social platform can show on
 * a portfolio, how they group in the customize panel's "Metrics Shown" picker,
 * and how the public "Social Accounts" grid (EPD-10113) renders them. Both the
 * picker and the grid read from here so they cannot drift.
 *
 * Two metric kinds share the picker's checkbox mechanics but drive different
 * surfaces:
 *  - `stat` metrics render a tile in the flat metric grid (their `get` reads the
 *    value off the legacy stats blob).
 *  - `demographic` metrics select which charts appear inside the Audience
 *    Demographics block (EPD-10117) — they render no tile. Since EPD-12034
 *    they are the block's only visibility control.
 *
 * The design prototype's catalogs list more stats than the legacy backend
 * actually returns (e.g. Reels Hook Rate, Monthly Viewers, Viral Post %). We
 * ship the prototype's group structure, order, labels and info-flags, populated
 * only with stats that have a real backing field — an unbacked stat would be a
 * permanent em-dash. The omitted stats are a data gap tracked on the tickets.
 */

import type { SocialStatsEntry } from "@/lib/queries/channel";

/** Platforms with a bespoke portfolio metric catalog. */
export const CATALOG_PLATFORMS = [
  "instagram",
  "tiktok",
  "youtube",
  "pinterest",
] as const;

export type CatalogPlatform = (typeof CATALOG_PLATFORMS)[number];

export function isCatalogPlatform(value: string): value is CatalogPlatform {
  return (CATALOG_PLATFORMS as readonly string[]).includes(value);
}

/**
 * Per-network metric ids the creator chose to hide (EPD-12113). A deny-list, so
 * a platform absent — or any metric added to the catalog later — shows by
 * default.
 */
export type HiddenMetricIds = Partial<Record<CatalogPlatform, string[]>>;

type Stats = NonNullable<SocialStatsEntry["stats"]>;

export type StatKind = "count" | "percent" | "ratio" | "text";

/** The demographic charts the demographics group can toggle (EPD-10117). */
export type DemographicKey = "age" | "gender" | "country" | "city";

interface BaseMetric {
  id: string;
  label: string;
}

export interface StatMetric extends BaseMetric {
  type: "stat";
  kind: StatKind;
  get: (s: Stats) => number | string | null | undefined;
}

export interface DemographicMetric extends BaseMetric {
  type: "demographic";
  demographic: DemographicKey;
}

export type MetricDef = StatMetric | DemographicMetric;

export interface MetricGroup {
  id: string;
  title: string;
  metrics: MetricDef[];
}

// ── formatting ──────────────────────────────────────────────────────────────

const compact = new Intl.NumberFormat("en", {
  notation: "compact",
  maximumFractionDigits: 1,
});

/**
 * Compact count for the tab follower badges (null → em-dash). A negative count
 * is a legacy "never fetched" sentinel (e.g. Pinterest `profile_reach`
 * defaults to -1, and backs `reach`), not data, so it renders as missing too.
 */
export function fmtCount(v: number | null | undefined): string {
  return v == null || v < 0 ? "—" : compact.format(v);
}

/**
 * Normalise a legacy value the backend sends in percentage points (or scaled
 * ×100) back to the unit the formatter expects. The legacy stats are not all
 * on one scale (EPD-12093): `engagementRatio` is a decimal fraction (0.05 = 5%)
 * but `impressionsRatio` and `storiesImpressionsRatio` are percentage points
 * (`impressions / followers * 100`), and `likesCommentsRatio` is
 * `likes / comments * 100`. Aspirex divides all three by 100 before display;
 * the portfolio must too, or the tiles read 100× too high.
 */
function unscalePercentPoints(v: number | null | undefined): number | null {
  return v == null ? null : v / 100;
}

export function formatStat(metric: StatMetric, s: Stats): string {
  const raw = metric.get(s);
  if (raw == null || raw === "") return "—";
  switch (metric.kind) {
    case "count":
      if (typeof raw !== "number") return String(raw);
      // Counts are never negative; a negative is a "no data" sentinel.
      return raw < 0 ? "—" : compact.format(raw);
    case "percent":
      // Percent getters return a decimal fraction (0.05 = 5%) — fields the
      // backend sends in percentage points are unscaled in their getter (see
      // unscalePercentPoints) — so scale unconditionally.
      return typeof raw === "number"
        ? `${(raw * 100).toFixed(1)}%`
        : String(raw);
    case "ratio":
      if (typeof raw !== "number") return String(raw);
      // Below 1 (e.g. more comments than likes) whole-number rounding would
      // read "0:1", so keep one decimal there.
      return raw < 1 ? `${Number(raw.toFixed(1))}:1` : `${Math.round(raw)}:1`;
    case "text":
      return String(raw);
  }
}

// ── stat builders ───────────────────────────────────────────────────────────
// Follower count comes from `followerCount`, falling back to `reach` (the legacy
// backend defines `reach == follower_count` for accounts without a distinct
// follower field).

function stat(
  id: string,
  label: string,
  kind: StatKind,
  get: StatMetric["get"],
): StatMetric {
  return { type: "stat", id, label, kind, get };
}

function demographic(
  id: string,
  label: string,
  key: DemographicKey,
): DemographicMetric {
  return { type: "demographic", id, label, demographic: key };
}

// ── per-platform groups ─────────────────────────────────────────────────────
// Structure, order, labels and info-flags follow EPD-10114/10115/10116; only
// stats the backend supplies are listed (see file header).

const INSTAGRAM_GROUPS: MetricGroup[] = [
  {
    id: "ig.summary",
    title: "Summary",
    metrics: [
      stat(
        "ig.followers",
        "Followers",
        "count",
        (s) => s.followerCount ?? s.reach,
      ),
      stat("ig.totalPosts", "Total Posts", "count", (s) => s.contentCount),
      stat(
        "ig.authenticity",
        "Audience Authenticity",
        "text",
        (s) => s.audienceAuthenticityLabel,
      ),
    ],
  },
  {
    id: "ig.engagement",
    title: "Engagement",
    metrics: [
      stat(
        "ig.engagementRate",
        "Engagement Rate",
        "percent",
        (s) => s.engagementRatio,
      ),
      stat(
        "ig.avgComments",
        "Avg Comments",
        "count",
        (s) => s.averageCommentsCount,
      ),
      stat("ig.saves", "Avg Saves", "count", (s) => s.saves),
      stat("ig.likesToComment", "Likes to Comment Ratio", "ratio", (s) =>
        unscalePercentPoints(s.likesCommentsRatio),
      ),
    ],
  },
  {
    id: "ig.reach",
    title: "Reach",
    metrics: [
      stat("ig.reach", "Reach", "count", (s) => s.reach),
      stat("ig.impressionRate", "Impression Rate", "percent", (s) =>
        unscalePercentPoints(s.impressionsRatio),
      ),
      stat(
        "ig.avgStoryImpressions",
        "Avg Story Impressions",
        "count",
        (s) => s.recentStoriesImpressions,
      ),
      stat("ig.storyImpressionRate", "Story Impression Rate", "percent", (s) =>
        unscalePercentPoints(s.storiesImpressionsRatio),
      ),
    ],
  },
  {
    id: "ig.demographics",
    title: "Audience Demographics",
    metrics: [
      demographic("ig.demo.gender", "Gender Distribution", "gender"),
      demographic("ig.demo.age", "Age Distribution", "age"),
      demographic("ig.demo.country", "Top Countries", "country"),
      demographic("ig.demo.city", "Top Cities", "city"),
    ],
  },
];

const TIKTOK_GROUPS: MetricGroup[] = [
  {
    id: "tt.summary",
    title: "Summary",
    metrics: [
      stat(
        "tt.followers",
        "Followers",
        "count",
        (s) => s.followerCount ?? s.reach,
      ),
      stat("tt.totalVideos", "Total Videos", "count", (s) => s.contentCount),
      stat(
        "tt.engagementRate",
        "Engagement Rate",
        "percent",
        (s) => s.engagementRatio,
      ),
      stat("tt.totalLikes", "Total Likes", "count", (s) => s.likes),
      stat(
        "tt.avgComments",
        "Avg Comments",
        "count",
        (s) => s.averageCommentsCount,
      ),
      stat(
        "tt.typicalViews",
        "Typical Views",
        "count",
        (s) => s.recentGeometricMeanViewCount,
      ),
    ],
  },
  {
    id: "tt.demographics",
    title: "Audience Demographics",
    metrics: [
      demographic("tt.demo.age", "Age Distribution", "age"),
      demographic("tt.demo.country", "Top Countries", "country"),
    ],
  },
];

const YOUTUBE_GROUPS: MetricGroup[] = [
  {
    id: "yt.summary",
    title: "Summary",
    metrics: [
      stat(
        "yt.subscribers",
        "Subscribers",
        "count",
        (s) => s.followerCount ?? s.reach,
      ),
      stat("yt.totalVideos", "Total Videos", "count", (s) => s.contentCount),
      stat(
        "yt.engagementRate",
        "Engagement Rate",
        "percent",
        (s) => s.engagementRatio,
      ),
      stat(
        "yt.typicalViews",
        "Typical Views",
        "count",
        (s) => s.recentGeometricMeanViewCount,
      ),
      stat(
        "yt.avgComments",
        "Avg Comments",
        "count",
        (s) => s.averageCommentsCount,
      ),
    ],
  },
  {
    id: "yt.demographics",
    title: "Audience Demographics",
    metrics: [
      demographic("yt.demo.age", "Age Distribution", "age"),
      demographic("yt.demo.gender", "Gender Distribution", "gender"),
      demographic("yt.demo.country", "Top Countries", "country"),
    ],
  },
];

const PINTEREST_GROUPS: MetricGroup[] = [
  {
    id: "pt.summary",
    title: "Summary",
    metrics: [
      stat(
        "pt.followers",
        "Followers",
        "count",
        (s) => s.followerCount ?? s.reach,
      ),
      stat(
        "pt.engagementRate",
        "Engagement Rate",
        "percent",
        (s) => s.engagementRatio,
      ),
      stat("pt.avgSavesPerPin", "Avg Saves per Pin", "count", (s) => s.saves),
      stat(
        "pt.avgCommentsPerPin",
        "Avg Comments per Pin",
        "count",
        (s) => s.averageCommentsCount,
      ),
      stat(
        "pt.avgClicksPerPin",
        "Avg Clicks per Pin",
        "count",
        (s) => s.averageClicks,
      ),
    ],
  },
];

const GROUPS_BY_PLATFORM: Record<CatalogPlatform, MetricGroup[]> = {
  instagram: INSTAGRAM_GROUPS,
  tiktok: TIKTOK_GROUPS,
  youtube: YOUTUBE_GROUPS,
  pinterest: PINTEREST_GROUPS,
};

// Fallback grid for connected platforms with no bespoke catalog (e.g. Twitter,
// Facebook). They have no "Metrics Shown" picker, so every stat always shows.
const DEFAULT_GRID_METRICS: StatMetric[] = [
  stat(
    "default.followers",
    "Followers",
    "count",
    (s) => s.followerCount ?? s.reach,
  ),
  stat(
    "default.engagementRate",
    "Engagement Rate",
    "percent",
    (s) => s.engagementRatio,
  ),
  stat(
    "default.avgComments",
    "Avg Comments",
    "count",
    (s) => s.averageCommentsCount,
  ),
  stat("default.totalLikes", "Total Likes", "count", (s) => s.likes),
  stat("default.totalPosts", "Total Posts", "count", (s) => s.contentCount),
  stat("default.reach", "Reach", "count", (s) => s.reach),
  stat("default.impressions", "Impressions", "count", (s) => s.impressions),
];

// ── lookups ─────────────────────────────────────────────────────────────────

export function metricGroupsFor(platform: CatalogPlatform): MetricGroup[] {
  return GROUPS_BY_PLATFORM[platform];
}

/** Every selectable metric id for a platform, in catalog order. */
export function allMetricIdsFor(platform: CatalogPlatform): string[] {
  return metricGroupsFor(platform).flatMap((g) => g.metrics.map((m) => m.id));
}

/** The demographic chart metric ids for a platform, in catalog order. */
export function demographicMetricIdsFor(platform: CatalogPlatform): string[] {
  return metricGroupsFor(platform)
    .flatMap((g) => g.metrics)
    .filter((m) => m.type === "demographic")
    .map((m) => m.id);
}

/** The flat stat (grid) metrics for a platform, in catalog order. */
export function gridMetricsFor(platform: CatalogPlatform): StatMetric[] {
  return metricGroupsFor(platform)
    .flatMap((g) => g.metrics)
    .filter((m): m is StatMetric => m.type === "stat");
}

/**
 * Whether a metric is shown: everything is, unless the creator hid it. Hidden
 * ids no longer in the catalog are simply never matched.
 */
export function isMetricEnabled(
  hidden: HiddenMetricIds,
  platform: CatalogPlatform,
  id: string,
): boolean {
  return !(hidden[platform]?.includes(id) ?? false);
}

/** Stat (grid) metrics to render for a platform, minus the hidden ones. */
export function visibleGridMetrics(
  hidden: HiddenMetricIds,
  platform: CatalogPlatform,
): StatMetric[] {
  return gridMetricsFor(platform).filter((m) =>
    isMetricEnabled(hidden, platform, m.id),
  );
}

/**
 * Stat (grid) metrics for any connected platform: the catalog minus hidden ids
 * for the four bespoke networks, or the generic fallback grid for the rest.
 */
export function visibleGridMetricsForPlatform(
  hidden: HiddenMetricIds,
  platform: string,
): StatMetric[] {
  return isCatalogPlatform(platform)
    ? visibleGridMetrics(hidden, platform)
    : DEFAULT_GRID_METRICS;
}

/** Demographic charts shown for a platform (those not hidden). */
export function enabledDemographics(
  hidden: HiddenMetricIds,
  platform: CatalogPlatform,
): Set<DemographicKey> {
  const enabled = new Set<DemographicKey>();
  for (const group of metricGroupsFor(platform)) {
    for (const metric of group.metrics) {
      if (
        metric.type === "demographic" &&
        isMetricEnabled(hidden, platform, metric.id)
      ) {
        enabled.add(metric.demographic);
      }
    }
  }
  return enabled;
}

/**
 * Whether any demographic chart is shown on any catalog platform — the only
 * gate on the Audience Demographics block since its section toggle was retired
 * (EPD-12034).
 */
export function anyDemographicsEnabled(hidden: HiddenMetricIds): boolean {
  return CATALOG_PLATFORMS.some(
    (platform) => enabledDemographics(hidden, platform).size > 0,
  );
}
